import * as d3 from "d3";
import { drawCircles } from "@/components/VivoPlots/functions/drawPlotData/utils/drawCircles";
import { getNormalizedSelectedId } from "@/components/VivoPlots/functions/utils/selectedIdUtils";

interface ScaleEntry {
  type: string;
  position?: string;
  scale: d3.ScaleContinuousNumeric<number, number> & { domain: () => number[] };
}

interface PlotConfig {
  showDiagonalLine?: boolean;
  selectedIdName?: string;
  selectedIdValue?: unknown;
  setClickedSubject?: (id: string) => void;
  onPointActivate?: (entityKey: string, event: MouseEvent) => void;
  ssjIdValue?: string;
  palette?: string;
  [key: string]: unknown;
}

interface DataPoint {
  x: number | Date;
  y: number | Date;
  [key: string]: unknown;
}

interface FillScale {
  type?: string;
  scale?: (value: unknown) => string;
  strokeScale?: (value: unknown) => string;
  [key: string]: unknown;
}

export const drawScatterPlot = (
  plotClassName: string,
  data: DataPoint[],
  scales: ScaleEntry[],
  plotConfig: PlotConfig,
) => {
  const normalizedId = getNormalizedSelectedId(plotConfig);
  const svg = d3.select(`svg.${plotClassName}`);

  const bottomScale = scales.find(
    (scale) =>
      (scale.type === "continuous" || scale.type === "time") &&
      scale.position === "bottom",
  )!.scale;
  const leftScale = scales.find(
    (scale) =>
      (scale.type === "continuous" || scale.type === "time") &&
      scale.position === "left",
  )!.scale;

  const fillScale: FillScale | undefined = scales?.find(
    (scale) => scale?.type === "fill",
  ) as FillScale | undefined;

  // x=y diagonal reference line
  const showDiagonal = plotConfig?.showDiagonalLine !== false;
  svg.selectAll("line.diagonal-reference").remove();
  if (showDiagonal) {
    // Normalize to [min, max] — the Y domain is stored reversed ([max, min]) so that
    // larger values map to smaller pixel y coordinates (top of chart).
    const xDomain = bottomScale.domain() as [number, number];
    const yDomain = leftScale.domain() as [number, number];
    const xMin = Math.min(...xDomain);
    const xMax = Math.max(...xDomain);
    const yMin = Math.min(...yDomain);
    const yMax = Math.max(...yDomain);
    const lineMin = Math.max(xMin, yMin);
    const lineMax = Math.min(xMax, yMax);
    if (lineMin < lineMax) {
      svg
        .selectAll("line.diagonal-reference")
        .data([null])
        .join("line")
        .classed("diagonal-reference", true)
        .attr("x1", bottomScale(lineMin))
        .attr("x2", bottomScale(lineMax))
        .attr("y1", leftScale(lineMin))
        .attr("y2", leftScale(lineMax))
        .attr("stroke", "#ccc")
        .attr("stroke-width", 1.5)
        .attr("stroke-dasharray", "6 4")
        .attr("pointer-events", "none");
    }
  }

  const scatterData = data.map((d) => {
    return {
      x: bottomScale(d.x),
      y: leftScale(d.y),
      data: d,
    };
  });

  const scatterContainer = svg
    .selectAll("g.scatter")
    .data([{ points: scatterData }])
    .join("g")
    .classed("scatter", true);

  drawCircles(
    scatterContainer as any,
    normalizedId,
    plotConfig as any,
    plotClassName,
    fillScale,
    true,
  );
};
